/** 11 §4 sparks: short monochrome strokes, seeded per event so every client
 *  sees the same burst. World-space xz at DISC_FLIGHT_Y; projected at draw. */
import { FX_MAX_SPARKS, FX_SPARK_CLASH, FX_SPARK_LIFE_MAX_MS, FX_SPARK_LIFE_MIN_MS, FX_SPARK_PILLAR, FX_SPARK_SHIELD_LATE, FX_SPARK_SHIELD_PERFECT, FX_SPARK_WALL } from './constants.js'
import { fxRng } from './rng.js'

export type SparkKind = 'wall' | 'pillar' | 'shieldLate' | 'shieldPerfect' | 'clash' | 'dust' | 'whoosh'

export interface Spark {
  kind: SparkKind
  x: number; z: number
  /** Direction (unit) and length in metres. */
  dx: number; dz: number; len: number
  bornAt: number; lifeMs: number
  /** For the perfect ring: collapse inward. */
  collapse: boolean
  /** Accent team for the 1-frame perfect brighten (null = ink). */
  team: 0 | 1 | null
}

export const SPARK_COUNTS: Record<SparkKind, number> = {
  wall: FX_SPARK_WALL, pillar: FX_SPARK_PILLAR, shieldLate: FX_SPARK_SHIELD_LATE, shieldPerfect: FX_SPARK_SHIELD_PERFECT, clash: FX_SPARK_CLASH, dust: 3, whoosh: 1,
}

export class SparkPool {
  readonly sparks: Spark[] = []

  /** `nx,nz` = surface normal (wall/pillar) or tangent (shield) or disc velocity (whoosh). */
  burst(kind: SparkKind, eventKey: string | number, x: number, z: number, nx: number, nz: number, now: number, team: 0 | 1 | null = null): Spark[] {
    const rng = fxRng(eventKey)
    const n = SPARK_COUNTS[kind]
    const nl = Math.hypot(nx, nz) || 1
    nx /= nl; nz /= nl
    const base = Math.atan2(nz, nx)
    const out: Spark[] = []
    for (let i = 0; i < n; i++) {
      const life = FX_SPARK_LIFE_MIN_MS + rng() * (FX_SPARK_LIFE_MAX_MS - FX_SPARK_LIFE_MIN_MS)
      let ang: number, len: number, collapse = false, lifeMs = life
      switch (kind) {
        case 'wall': ang = base + (rng() * 2 - 1) * (40 * Math.PI / 180); len = 0.25 + rng() * 0.35; break
        case 'pillar': ang = base + (rng() * 2 - 1) * (60 * Math.PI / 180); len = 0.25 + rng() * 0.4; break
        case 'shieldLate': ang = base + (i % 2 ? Math.PI : 0) + (rng() - 0.5) * 0.3; len = 0.2 + rng() * 0.25; break
        case 'shieldPerfect': ang = (i / n) * Math.PI * 2; len = 0.35; collapse = true; lifeMs = 120; break
        case 'clash': ang = (Math.floor(i / 2) % 4) * (Math.PI / 2) + Math.PI / 4 + (i % 2 ? Math.PI : 0) * 0 + (rng() - 0.5) * 0.15; len = 0.3 + rng() * 0.3; lifeMs = 160; break
        case 'dust': ang = -Math.PI / 2 + (i - 1) * 0.6 + (rng() - 0.5) * 0.2; len = 0.15 + rng() * 0.1; lifeMs = 120; break
        default: ang = base; len = 1.2; lifeMs = 100
      }
      out.push({ kind, x, z, dx: Math.cos(ang), dz: Math.sin(ang), len, bornAt: now, lifeMs, collapse, team })
    }
    for (const s of out) { this.sparks.push(s); if (this.sparks.length > FX_MAX_SPARKS) this.sparks.shift() }
    return out
  }

  /** Drop expired sparks. */
  prune(now: number): void {
    let w = 0
    for (const s of this.sparks) if (now - s.bornAt < s.lifeMs) this.sparks[w++] = s
    this.sparks.length = w
  }

  clear(): void { this.sparks.length = 0 }
  get size(): number { return this.sparks.length }
}
